import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import PageLoader from "@/components/base/Loading";
import ProfileForm from "@/pages/settings/components/ProfileForm";
import PasswordForm from "@/pages/settings/components/PasswordForm";
import { useSettings } from "@/pages/settings/hooks/useSettings";
import { formatFullDate } from "@/lib/format";

interface Notice {
  text: string;
  tone: "success" | "error";
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const { profile, email, loading, savingProfile, savingPassword, saveProfile, changePassword } = useSettings();
  const [notice, setNotice] = useState<Notice | null>(null);

  const notify = useCallback((text: string, tone: "success" | "error" = "success") => {
    setNotice({ text, tone });
    window.setTimeout(() => setNotice(null), 4000);
  }, []);

  const securityTips = [t("settings.securityTip1"), t("settings.securityTip2"), t("settings.securityTip3")];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("settings.breadcrumbHome")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">{t("settings.title")}</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
              {t("settings.title")}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {t("settings.subtitle")}
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {loading ? (
            <PageLoader label={t("settings.loading")} />
          ) : (
            <div className="grid gap-6 lg:grid-cols-5">
              <div className="lg:col-span-3">
                <ProfileForm
                  fullName={profile?.full_name ?? ""}
                  company={profile?.company ?? ""}
                  email={email}
                  role={profile?.role ?? "customer"}
                  createdAt={profile?.created_at ? formatFullDate(profile.created_at) : "—"}
                  saving={savingProfile}
                  onSave={saveProfile}
                  notify={notify}
                />
              </div>

              <div className="flex flex-col gap-6 lg:col-span-2">
                <PasswordForm saving={savingPassword} onChangePassword={changePassword} notify={notify} />

                <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                      <i className="ri-shield-keyhole-line text-base" />
                    </span>
                    <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("settings.securityTitle")}</h2>
                  </div>
                  <ul className="mt-3 space-y-2.5">
                    {securityTips.map((tip) => (
                      <li key={tip} className="flex items-start gap-2 text-xs leading-relaxed text-foreground-700">
                        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                          <i className="ri-checkbox-circle-line" />
                        </span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("settings.quickTitle")}</h2>
                  <div className="mt-3 flex flex-col gap-2">
                    <Link
                      to="/licenses"
                      className="flex items-center justify-between rounded-md border border-background-200 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      <span className="flex items-center gap-2">
                        <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                          <i className="ri-key-2-line" />
                        </span>
                        {t("settings.quickLicenses")}
                      </span>
                      <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                        <i className="ri-arrow-right-s-line" />
                      </span>
                    </Link>
                    <Link
                      to="/apply"
                      className="flex items-center justify-between rounded-md border border-background-200 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      <span className="flex items-center gap-2">
                        <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                          <i className="ri-send-plane-line" />
                        </span>
                        {t("settings.quickApply")}
                      </span>
                      <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                        <i className="ri-arrow-right-s-line" />
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 px-4">
          <div
            className={`lh-fade-up flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm shadow-sm ${
              notice.tone === "success" ? "bg-primary-600 text-background-50" : "bg-accent-600 text-background-50"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={notice.tone === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
            </span>
            {notice.text}
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}